import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/auth-context'
import { useSelfChatUserId } from './use-self-chat-user-id'
import { useVoiceCallSession } from './use-voice-call-session'
import type { VoiceCallPeer } from '../types/voice-call'
import {
  getRemoteCallPeerId,
  resolveRemoteCallPeer,
  resolveRemoteCallPeerSync,
} from '../utils/voice-call-peer'

function isLikelySelfPeer(peer: VoiceCallPeer, currentUserId: string, selfDisplayName?: string): boolean {
  if (peer.userId === currentUserId) return true
  if (!selfDisplayName || peer.displayName === 'Unknown') return false
  return peer.displayName.trim().toLowerCase() === selfDisplayName.trim().toLowerCase()
}

export function useRemoteCallPeer(): VoiceCallPeer | null {
  const session = useVoiceCallSession()
  const { user } = useAuth()
  const authUserId = user?.userId
  const selfDisplayName = user?.displayName
  const currentUserId = useSelfChatUserId(authUserId)

  const [peer, setPeer] = useState<VoiceCallPeer | null>(() => {
    if (!session || !currentUserId) return null
    return resolveRemoteCallPeerSync(session, currentUserId)
  })

  useEffect(() => {
    if (!session || !currentUserId) {
      setPeer(null)
      return
    }

    const syncPeer = resolveRemoteCallPeerSync(session, currentUserId)
    const remoteUserId = getRemoteCallPeerId(session, currentUserId)
    const needsAsyncLookup =
      syncPeer.displayName === 'Unknown' ||
      isLikelySelfPeer(syncPeer, currentUserId, selfDisplayName) ||
      syncPeer.userId !== remoteUserId

    if (!needsAsyncLookup) {
      setPeer(syncPeer)
      return
    }

    setPeer(syncPeer)

    let cancelled = false
    void resolveRemoteCallPeer(session, currentUserId).then((resolved) => {
      if (cancelled) return
      if (isLikelySelfPeer(resolved, currentUserId, selfDisplayName)) {
        setPeer({ ...resolved, userId: remoteUserId, displayName: 'Unknown' })
        return
      }
      setPeer(resolved)
    })
    return () => {
      cancelled = true
    }
  }, [session, currentUserId, selfDisplayName])

  return peer
}
